import React, { useState, useEffect, useRef } from "react";
import { Alert, Upload, UploadFile, Radio } from "antd";
import {
  UploadOutlined,
  FileOutlined,
  DeleteOutlined,
  BoldOutlined,
  ItalicOutlined,
  UnderlineOutlined,
  StrikethroughOutlined,
  AlignLeftOutlined,
  AlignCenterOutlined,
  AlignRightOutlined,
  OrderedListOutlined,
  UnorderedListOutlined,
  LinkOutlined,
  ClearOutlined,
  UndoOutlined,
  RedoOutlined,
} from "@ant-design/icons";

interface JobDescriptionUploadProps {
  jobDescription: UploadFile | null;
  onUpload: (file: File) => void;
  onDelete: () => void;
  jobDescriptionText?: string;
  onTextChange?: (text: string) => void;
  descriptionInputType?: string;
  onDescriptionTypeChange?: (type: string) => void;
}

export const JobDescriptionUpload: React.FC<JobDescriptionUploadProps> = ({
  jobDescription,
  onUpload,
  onDelete,
  jobDescriptionText = "",
  onTextChange,
  descriptionInputType: externalDescriptionInputType = "upload",
  onDescriptionTypeChange,
}) => {
  const [error, setError] = useState<string | null>(null);
  const [pastedText, setPastedText] = useState<string>(jobDescriptionText);
  const [internalDescriptionInputType, setInternalDescriptionInputType] =
    useState<string>(externalDescriptionInputType);
  const [selectionStart, setSelectionStart] = useState<number>(0);
  const [selectionEnd, setSelectionEnd] = useState<number>(0);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [history, setHistory] = useState<string[]>([]);
  const [historyIndex, setHistoryIndex] = useState<number>(-1);

  // Sync with external prop
  useEffect(() => {
    setInternalDescriptionInputType(externalDescriptionInputType);
  }, [externalDescriptionInputType]);

  // Save history for undo/redo
  useEffect(() => {
    if (pastedText) {
      const newHistory = history.slice(0, historyIndex + 1);
      newHistory.push(pastedText);
      setHistory(newHistory);
      setHistoryIndex(newHistory.length - 1);
    }
  }, [pastedText]);

  const handleUploadJD = (file: File) => {
    if (file.size && file.size > 1024 * 1024) {
      setError(`${file.name} is larger than 1MB and was not uploaded.`);
      return Upload.LIST_IGNORE;
    }

    onUpload(file);
    return false;
  };

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const text = e.target.value;
    setPastedText(text);
    onTextChange?.(text);
    setSelectionStart(e.target.selectionStart);
    setSelectionEnd(e.target.selectionEnd);
  };

  const handleTypeChange = (e: any) => {
    const value = e.target.value;
    setInternalDescriptionInputType(value);
    onDescriptionTypeChange?.(value);

    if (value === "upload") {
      setPastedText("");
      onTextChange?.("");
    } else if (value === "manual") {
      if (jobDescription) {
        onDelete?.();
      }
    }
  };

  const handleRemoveFile = () => {
    onDelete();
  };

  const formatText = (format: string) => {
    if (!textareaRef.current) return;

    const textarea = textareaRef.current;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = pastedText.substring(start, end);

    if (!selectedText) return;

    let formattedText = selectedText;
    let wrapChar = "";

    switch (format) {
      case "bold":
        wrapChar = "**";
        break;
      case "italic":
        wrapChar = "*";
        break;
      case "underline":
        wrapChar = "__";
        break;
      case "strikethrough":
        wrapChar = "~~";
        break;
      default:
        return;
    }

    const alreadyFormatted =
      pastedText.substring(start - wrapChar.length, start) === wrapChar &&
      pastedText.substring(end, end + wrapChar.length) === wrapChar;

    if (alreadyFormatted) {
      const newText =
        pastedText.substring(0, start - wrapChar.length) +
        selectedText +
        pastedText.substring(end + wrapChar.length);
      setPastedText(newText);
      onTextChange?.(newText);
      setTimeout(() => {
        textarea.selectionStart = start - wrapChar.length;
        textarea.selectionEnd = end - wrapChar.length;
        textarea.focus();
      }, 0);
    } else {
      const newText =
        pastedText.substring(0, start) +
        wrapChar +
        selectedText +
        wrapChar +
        pastedText.substring(end);
      setPastedText(newText);
      onTextChange?.(newText);
      setTimeout(() => {
        textarea.selectionStart = start + wrapChar.length;
        textarea.selectionEnd = end + wrapChar.length;
        textarea.focus();
      }, 0);
    }
  };

  const insertList = (type: "ordered" | "unordered") => {
    if (!textareaRef.current) return;

    const textarea = textareaRef.current;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = pastedText.substring(start, end);

    const lines = selectedText.split("\n");
    let formattedLines = lines.map((line, index) => {
      if (type === "ordered") {
        return `${index + 1}. ${line}`;
      } else {
        return `• ${line}`;
      }
    });

    const newText =
      pastedText.substring(0, start) +
      formattedLines.join("\n") +
      pastedText.substring(end);

    setPastedText(newText);
    onTextChange?.(newText);
  };

  const insertLink = () => {
    if (!textareaRef.current) return;

    const textarea = textareaRef.current;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = pastedText.substring(start, end);

    const url = prompt("Enter URL:", "https://");
    if (!url) return;

    const linkText = selectedText || "link";
    const newText =
      pastedText.substring(0, start) +
      `[${linkText}](${url})` +
      pastedText.substring(end);

    setPastedText(newText);
    onTextChange?.(newText);
  };

  const alignText = (alignment: "left" | "center" | "right") => {
    if (!textareaRef.current) return;

    const textarea = textareaRef.current;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = pastedText.substring(start, end);

    if (!selectedText) return;

    const lines = selectedText.split("\n");
    const alignedLines = lines.map((line) => {
      switch (alignment) {
        case "center":
          return `   ${line}   `;
        case "right":
          return `      ${line}`;
        default:
          return line;
      }
    });

    const newText =
      pastedText.substring(0, start) +
      alignedLines.join("\n") +
      pastedText.substring(end);

    setPastedText(newText);
    onTextChange?.(newText);
  };

  const clearFormatting = () => {
    if (!textareaRef.current) return;

    const textarea = textareaRef.current;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = pastedText.substring(start, end);

    if (!selectedText) return;

    const cleanedText = selectedText
      .replace(/\*\*(.*?)\*\*/g, "$1")
      .replace(/\*(.*?)\*/g, "$1")
      .replace(/__(.*?)__/g, "$1")
      .replace(/~~(.*?)~~/g, "$1")
      .replace(/\[(.*?)\]\((.*?)\)/g, "$1")
      .replace(/^[\d]+\.\s/gm, "")
      .replace(/^[•·]\s/gm, "");

    const newText =
      pastedText.substring(0, start) + cleanedText + pastedText.substring(end);

    setPastedText(newText);
    onTextChange?.(newText);
  };

  const undo = () => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      setPastedText(history[newIndex]);
      onTextChange?.(history[newIndex]);
    }
  };

  const redo = () => {
    if (historyIndex < history.length - 1) {
      const newIndex = historyIndex + 1;
      setHistoryIndex(newIndex);
      setPastedText(history[newIndex]);
      onTextChange?.(history[newIndex]);
    }
  };

  const ToolbarButton = ({
    icon,
    onClick,
    title,
    active = false,
  }: {
    icon: React.ReactNode;
    onClick: () => void;
    title: string;
    active?: boolean;
  }) => (
    <button
      onClick={onClick}
      title={title}
      className={`p-2 rounded hover:bg-gray-100 transition ${
        active ? "bg-blue-50 text-blue-600" : "text-gray-600"
      }`}
    >
      {icon}
    </button>
  );

  const ToolbarDivider = () => <div className="w-px h-6 bg-gray-300 mx-1" />;

  return (
    <div className="border rounded-lg bg-white p-6 shadow-md">
      <h3 className="text-lg font-semibold mb-4">
        How would you like to provide the job description?
      </h3>

      {error && (
        <Alert
          message="Upload Error"
          description={error}
          type="error"
          showIcon
          closable
          className="mb-4"
          onClose={() => setError(null)}
        />
      )}

      <Radio.Group
        onChange={handleTypeChange}
        value={internalDescriptionInputType}
        className="mb-4"
      >
        <Radio value="upload">Upload Description File</Radio>
        <Radio value="manual">Paste Text</Radio>
      </Radio.Group>

      {internalDescriptionInputType === "upload" && (
        <div className="upload-section">
          <Upload
            beforeUpload={handleUploadJD}
            showUploadList={false}
            disabled={!!jobDescription}
            accept=".pdf,.doc,.docx,.txt"
          >
            <div
              className={`
                flex flex-col justify-center items-center border-2 border-dashed rounded-lg 
                p-8 text-center cursor-pointer transition 
                ${
                  jobDescription
                    ? "border-green-300 cursor-not-allowed bg-green-50"
                    : "border-blue-300 hover:bg-blue-50"
                }
              `}
            >
              {!jobDescription ? (
                <UploadOutlined className="text-3xl mb-2 text-blue-500" />
              ) : (
                <span className="mb-2 inline-flex items-center justify-center bg-green-500 text-white rounded-full w-12 h-12">
                  <span className="text-lg">✓</span>
                </span>
              )}

              <p className="text-gray-600 font-medium">
                {jobDescription
                  ? "Job Description Uploaded"
                  : "Click to upload or drag and drop"}
              </p>
              <p className="text-xs text-gray-400 mt-1">
                {jobDescription
                  ? jobDescription.name
                  : "Supported formats: PDF, DOC, DOCX, TXT (Max 1MB)"}
              </p>
            </div>
          </Upload>

          {!jobDescription && (
            <p className="mt-4 text-center text-sm text-gray-500 italic">
              ⚠️ Only one document is allowed for Job Description.
            </p>
          )}

          {jobDescription && (
            <div className="mt-4 border rounded-lg bg-gray-50 p-4 shadow-sm text-gray-600 text-sm">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <FileOutlined className="mr-2 text-blue-500" />
                  <div>
                    <div className="font-medium">{jobDescription.name}</div>
                    <div className="text-xs text-gray-400">
                      {((jobDescription.size || 0) / 1024).toFixed(2)} KB
                    </div>
                  </div>
                </div>
                <DeleteOutlined
                  onClick={handleRemoveFile}
                  className="text-red-500 cursor-pointer hover:text-red-700 text-lg"
                />
              </div>
            </div>
          )}
        </div>
      )}

      {internalDescriptionInputType === "manual" && (
        <div className="manual-section">
          <div className="border border-b-0 border-gray-300 rounded-t-lg bg-gray-50 p-2 flex flex-wrap items-center gap-1">
            <ToolbarButton
              icon={<BoldOutlined />}
              onClick={() => formatText("bold")}
              title="Bold (Ctrl+B)"
            />
            <ToolbarButton
              icon={<ItalicOutlined />}
              onClick={() => formatText("italic")}
              title="Italic (Ctrl+I)"
            />
            <ToolbarButton
              icon={<UnderlineOutlined />}
              onClick={() => formatText("underline")}
              title="Underline (Ctrl+U)"
            />
            <ToolbarButton
              icon={<StrikethroughOutlined />}
              onClick={() => formatText("strikethrough")}
              title="Strikethrough"
            />

            <ToolbarDivider />

            <ToolbarButton
              icon={<AlignLeftOutlined />}
              onClick={() => alignText("left")}
              title="Align Left"
            />
            <ToolbarButton
              icon={<AlignCenterOutlined />}
              onClick={() => alignText("center")}
              title="Align Center"
            />
            <ToolbarButton
              icon={<AlignRightOutlined />}
              onClick={() => alignText("right")}
              title="Align Right"
            />

            <ToolbarDivider />

            <ToolbarButton
              icon={<UnorderedListOutlined />}
              onClick={() => insertList("unordered")}
              title="Bullet List"
            />
            <ToolbarButton
              icon={<OrderedListOutlined />}
              onClick={() => insertList("ordered")}
              title="Numbered List"
            />

            <ToolbarDivider />

            <ToolbarButton
              icon={<LinkOutlined />}
              onClick={insertLink}
              title="Insert Link"
            />
            <ToolbarButton
              icon={<ClearOutlined />}
              onClick={clearFormatting}
              title="Clear Formatting"
            />

            <ToolbarDivider />

            <ToolbarButton
              icon={<UndoOutlined />}
              onClick={undo}
              title="Undo (Ctrl+Z)"
            />
            <ToolbarButton
              icon={<RedoOutlined />}
              onClick={redo}
              title="Redo (Ctrl+Y)"
            />
          </div>

          <div className="border border-t-0 border-gray-300 rounded-b-lg p-3">
            <textarea
              ref={textareaRef}
              className="w-full min-h-[150px] focus:outline-none resize-none text-gray-700 font-sans"
              placeholder="Type your message here..."
              value={pastedText}
              onChange={handleTextChange}
              onSelect={(e) => {
                const target = e.target as HTMLTextAreaElement;
                setSelectionStart(target.selectionStart);
                setSelectionEnd(target.selectionEnd);
              }}
            />
          </div>

          {pastedText && pastedText.length > 0 && (
            <div className="mt-3 flex justify-between items-center text-sm">
              <span className="text-gray-500">
                Text extracted: {pastedText.length} characters
              </span>
              <span className="text-green-500 font-medium">
                ✓ Content pasted
              </span>
            </div>
          )}

          {!pastedText && (
            <div className="mt-3 text-sm text-gray-400">
              💡 Paste your job description text here
            </div>
          )}

          {/* Show preview of pasted text */}
          {pastedText && pastedText.length > 0 && (
            <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-sm text-green-700">
                ✓ Job description text ready ({pastedText.length} characters)
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
